import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faStop, faVolumeUp, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './drum-machine.css'; // Your custom CSS file

// Define paths to your drum sound files (relative to the public/ directory)
const drumSoundPaths = {
    kick: '/sounds/kick.wav',
    snare: '/sounds/snare.wav',
    hihat: '/sounds/hihat.wav',
};

const DRUM_TYPES = ['kick', 'snare', 'hihat'];
const NUM_STEPS = 16;
const INITIAL_BPM = 120; // Beats Per Minute

// Initial drum pattern (16 steps)
const initialPattern = {
    kick: [1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
    snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
    hihat: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
};

function DrumMachine() {
    const audioContextRef = useRef(null); // Stores the Web Audio API AudioContext
    const drumWorkletNodeRef = useRef(null); // Stores the AudioWorkletNode

    const scheduleIntervalId = useRef(null); // ID for the scheduling setInterval
    const nextNoteTime = useRef(0.0); // The time when the next note should play (in AudioContext time)
    const currentSchedulerStep = useRef(0); // The current step being scheduled

    const [isLoading, setIsLoading] = useState(true); // True while engine/module/sounds load
    const [error, setError] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [tempo, setTempo] = useState(INITIAL_BPM);
    const [currentVisualStep, setCurrentVisualStep] = useState(0); // For UI highlight
    const [pattern, setPattern] = useState(initialPattern);
    
    // Single state to indicate if the entire audio engine is ready after user interaction
    const [isAudioEngineReady, setIsAudioEngineReady] = useState(false);

    // --- Main Audio Engine Setup (AudioContext, Worklet Module, WorkletNode, Buffers) ---
    // This useEffect handles the complete initialization sequence.
    // It's designed to be resilient to React Strict Mode's double-invocation.
    useEffect(() => {
        let isMounted = true; // Flag to track if component is mounted

        const fullAudioEngineSetup = async () => {
            setIsLoading(true); // Ensure loading state is true at start
            setError(null); // Clear previous errors

            try {
                // Step 1: Create AudioContext if it doesn't exist or is closed.
                // This handles Strict Mode's double-invocation: a new context will be created
                // if the previous one was closed by cleanup.
                if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
                    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
                    console.log('AudioContext created (state:', audioContextRef.current.state + ')');
                }
                const audioCtx = audioContextRef.current;

                // Step 2: Load AudioWorklet Processor Module
                // addModule is idempotent, so calling it multiple times is safe.
                await audioCtx.audioWorklet.addModule('/drum-processor.js');
                console.log('AudioWorklet module loaded.');

                // CRITICAL FIX: Use setTimeout to defer WorkletNode creation.
                // This gives the browser a moment to stabilize after Strict Mode's double-mount/cleanup cycle.
                // It helps bypass "No execution context available" errors.
                if (isMounted) { // Ensure component is still mounted before setting timeout
                    setTimeout(async () => {
                        if (!isMounted) return; // Check mount status again inside timeout

                        // Step 3: Attempt to resume AudioContext (will actually resume on first user gesture)
                        if (audioCtx.state === 'suspended') {
                            await audioCtx.resume().then(() => {
                                console.log('AudioContext resumed automatically (initial attempt).');
                            }).catch(e => {
                                console.warn('AudioContext failed to resume automatically (expected in some browsers without user gesture):', e.message);
                                // This is common; user interaction (e.g., Play button click) will still resume it.
                            });
                        }
                        
                        // Step 4: Create AudioWorkletNode (only if not already created for this context instance)
                        if (!drumWorkletNodeRef.current) {
                            try {
                                drumWorkletNodeRef.current = new AudioWorkletNode(audioCtx, 'drum-processor');
                                drumWorkletNodeRef.current.connect(audioCtx.destination);
                                console.log('AudioWorkletNode created and connected.');

                                // Step 5: Load and decode drum sound files
                                const loadPromises = DRUM_TYPES.map(async (type) => {
                                    const response = await fetch(drumSoundPaths[type]);
                                    if (!response.ok) {
                                        throw new Error(`Impossibile caricare il suono ${type} da ${drumSoundPaths[type]}: ${response.statusText}`);
                                    }
                                    const arrayBuffer = await response.arrayBuffer();
                                    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
                                    return { type, buffer: audioBuffer };
                                });

                                const loadedSoundData = await Promise.all(loadPromises);
                                const transferableBuffers = {};
                                loadedSoundData.forEach(({ type, buffer }) => {
                                    transferableBuffers[type] = {
                                        sampleRate: buffer.sampleRate,
                                        length: buffer.length,
                                        numberOfChannels: buffer.numberOfChannels,
                                        channelData: Array.from({ length: buffer.numberOfChannels }, (_, i) => buffer.getChannelData(i).buffer)
                                    };
                                });
                                console.log('Tutti i file audio di batteria decodificati.');

                                // Step 6: Transfer decoded AudioBuffers (data) to the AudioWorklet
                                drumWorkletNodeRef.current.port.postMessage({
                                    type: 'init-buffers',
                                    buffers: transferableBuffers
                                }, Object.values(transferableBuffers).flatMap(b => b.channelData));
                                console.log('Dati buffer inviati all\'AudioWorklet.');

                                if (isMounted) { // Only update state if component is still mounted
                                    setIsAudioEngineReady(true); // Mark engine as fully initialized and ready
                                    setIsLoading(false); // All loading is complete
                                }
                            } catch (e) {
                                console.error('Errore durante la creazione del WorkletNode o caricamento suoni (deferito):', e);
                                if (isMounted) {
                                    setError(`Errore motore audio: impossibile inizializzare i suoni o il WorkletNode. Dettagli: ${e.message}`);
                                    setIsLoading(false);
                                }
                            }
                        } else {
                            // If WorkletNode already existed (e.g., in a subsequent Strict Mode pass or component re-render)
                            console.log("WorkletNode already existed, marking as ready.");
                            if (isMounted) {
                                setIsAudioEngineReady(true);
                                setIsLoading(false);
                            }
                        }
                    }, 10); // Small delay to allow browser to stabilize
                }

            } catch (err) {
                console.error("Errore fatale nella fase iniziale di setup audio:", err);
                if (isMounted) {
                    setError(`Errore iniziale motore audio: ${err.message}. Verifica console.`);
                    setIsLoading(false);
                }
            }
        };

        fullAudioEngineSetup();

        // Cleanup function for this useEffect.
        // This runs when the component unmounts OR when Strict Mode performs its dev-mode double-invocation.
        return () => {
            isMounted = false; // Set flag to false on unmount/cleanup
            console.log('Pulizia risorse audio (Main useEffect Cleanup)...');
            if (scheduleIntervalId.current) {
                clearInterval(scheduleIntervalId.current);
                scheduleIntervalId.current = null;
            }
            if (drumWorkletNodeRef.current) {
                drumWorkletNodeRef.current.disconnect();
                drumWorkletNodeRef.current = null;
            }
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close()
                    .then(() => console.log('AudioContext closed during cleanup.'))
                    .catch((e) => console.error('Error closing AudioContext during cleanup:', e));
            }
            audioContextRef.current = null; // Crucial to ensure a fresh context on re-mount

            // Reset states to initial values
            setIsLoading(true);
            setError(null);
            setIsPlaying(false);
            setCurrentVisualStep(0);
            setIsAudioEngineReady(false);
        };
    }, []); // Empty dependency array: runs once on mount/cleanup cycle

    // --- Sequencer Logic ---
    const scheduleNotes = useCallback(() => {
        const audioCtx = audioContextRef.current;
        const workletNode = drumWorkletNodeRef.current;
        
        // CRUCIAL: Ensure AudioContext and WorkletNode are ready before scheduling
        if (!audioCtx || !workletNode || !isAudioEngineReady || !isPlaying || isLoading || error) {
            return;
        }

        const lookahead = 0.1; // 100 ms
        const secondsPerBeat = 60.0 / tempo;
        const secondsPerStep = secondsPerBeat / 4; // Assuming 16th notes for steps

        // Schedule notes until nextNoteTime is ahead of current AudioContext time + lookahead
        while (nextNoteTime.current < audioCtx.currentTime + lookahead) {
            const currentStepInPattern = currentSchedulerStep.current % NUM_STEPS;
            setCurrentVisualStep(currentStepInPattern); // Update UI for current step

            DRUM_TYPES.forEach(drumType => {
                if (pattern[drumType] && pattern[drumType][currentStepInPattern] === 1) {
                    workletNode.port.postMessage({
                        type: 'play-drum',
                        drumType: drumType,
                        timestamp: nextNoteTime.current
                    });
                }
            });

            currentSchedulerStep.current = (currentSchedulerStep.current + 1) % NUM_STEPS;
            nextNoteTime.current += secondsPerStep;
        }
    }, [isAudioEngineReady, isPlaying, isLoading, error, tempo, pattern]); // Depend on isAudioEngineReady

    // Effect to start/stop the sequencer interval
    useEffect(() => {
        const scheduleInterval = 25; // How often to check the scheduler (25 ms)

        // Ensure all conditions are met before starting the scheduler
        if (isPlaying && !isLoading && !error && isAudioEngineReady && audioContextRef.current) {
            if (!scheduleIntervalId.current) {
                nextNoteTime.current = audioContextRef.current.currentTime;
                currentSchedulerStep.current = 0; // Reset step on start
                scheduleNotes(); // Call immediately to schedule first notes
            }
            scheduleIntervalId.current = setInterval(scheduleNotes, scheduleInterval);
            console.log('Sequencer scheduler started.');
        } else {
            if (scheduleIntervalId.current) {
                clearInterval(scheduleIntervalId.current);
                scheduleIntervalId.current = null;
            }
            setCurrentVisualStep(0); // Reset visual step when stopped
            nextNoteTime.current = 0.0; // Reset scheduling time
            currentSchedulerStep.current = 0; // Reset scheduling step
            console.log('Sequencer scheduler stopped.');
        }

        return () => {
            if (scheduleIntervalId.current) {
                clearInterval(scheduleIntervalId.current);
            }
        };
    }, [isPlaying, isLoading, error, isAudioEngineReady, scheduleNotes]); // Depend on isAudioEngineReady

    // Pattern Toggle Logic
    const toggleStep = useCallback((drumType, stepIndex) => {
        setPattern(prevPattern => ({
            ...prevPattern,
            [drumType]: prevPattern[drumType].map((value, idx) =>
                idx === stepIndex ? (value === 1 ? 0 : 1) : value
            )
        }));
    }, []);

    // Render loading, error, or drum machine UI
    if (isLoading) {
        return (
            <div className="drum-machine-container react app-loading-fallback">
                <div className="spinner"></div>
                <p>Caricamento motore audio e suoni...</p>
                {error && <p className="error-message">{error}</p>}
            </div>
        );
    }

    if (error) {
        return (
            <div className="drum-machine-container react error-state">
                <p className="error-message">{error}</p>
                <button onClick={() => window.location.reload()} className="play-stop-button" style={{ marginTop: '1rem' }}>
                    Ricarica Pagina
                </button>
            </div>
        );
    }

    // Main Drum Machine UI is always rendered (no initial button/overlay)
    return (
        <div className="drum-machine-container react">
            <h2>808 Drum Machine</h2>

            <div className="controls-section">
                {/* The Play/Stop button will now also implicitly trigger AudioContext resume if needed */}
                <button
                    onClick={() => {
                        // Attempt to resume AudioContext on first play click if it's suspended
                        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                            audioContextRef.current.resume().then(() => {
                                console.log('AudioContext resumed by Play/Stop button click.');
                            }).catch(e => {
                                console.error('Failed to resume AudioContext on Play/Stop click:', e);
                                setError('Impossibile avviare l\'audio. Errore: ' + e.message);
                            });
                        }
                        setIsPlaying(!isPlaying);
                    }}
                    className={`play-stop-button ${isPlaying ? 'playing' : ''}`}
                    // Button disabled until the entire audio engine is fully ready
                    disabled={!isAudioEngineReady}
                >
                    <FontAwesomeIcon icon={isPlaying ? faStop : faPlay} />
                    {isPlaying ? ' Stop' : ' Play'}
                </button>

                <div className="tempo-control">
                    <label htmlFor="tempo-slider">Tempo:</label>
                    <input
                        type="range"
                        id="tempo-slider"
                        min="60"
                        max="200"
                        step="1"
                        value={tempo}
                        onChange={(e) => setTempo(parseInt(e.target.value))}
                        className="range-input"
                    />
                    <span className="tempo-value">{tempo} BPM</span>
                </div>
            </div>

            <div className="sequencer-grid">
                {/* Step headers */}
                <div className="drum-label"></div> {/* Empty cell for alignment */}
                {Array.from({ length: NUM_STEPS }).map((_, idx) => (
                    <div key={`step-header-${idx}`} className={`step-header ${idx === currentVisualStep ? 'current' : ''}`}>
                        {idx + 1}
                    </div>
                ))}

                {/* Drum rows */}
                {DRUM_TYPES.map(drumType => (
                    <React.Fragment key={drumType}>
                        <div className="drum-label">{drumType.charAt(0).toUpperCase() + drumType.slice(1)}</div>
                        {Array.from({ length: NUM_STEPS }).map((_, idx) => (
                            <button
                                key={`${drumType}-${idx}`}
                                className={`step-button ${pattern[drumType][idx] === 1 ? 'active' : ''} ${idx === currentVisualStep ? 'current' : ''}`}
                                onClick={() => toggleStep(drumType, idx)}
                            >
                            </button>
                        ))}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

export default DrumMachine;
